import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserStateModel, UserStatePayload, UserStore } from '@hmax-order/frontend/state';
import { map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from './user.types';

@Injectable({ providedIn: 'root' })
export class UserService {
  private _httpClient = inject(HttpClient);
  private _userStore = inject(UserStore);
  private _isStateDefined = false;
  private readonly _user$: Observable<UserStateModel> = toObservable(this._userStore.getUser);

  constructor() {
    // Hidrata o store a partir do snapshot já persistido no localStorage.
    // Cuidado: NÃO reatribuir `getUser()` para dentro do próprio setter — no
    // bootstrap o store está nos defaults, então isso (a) marcaria
    // `_isStateDefined = true` cedo demais, matando o fallback de `/me` em
    // `get()`, e (b) gravaria os defaults por cima do snapshot, virando o `null`
    // que os guards esperam num objeto truthy vazio.
    const persistedUser = this.getUserFromLocalStorage();
    if (persistedUser) {
      this.user = persistedUser;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for user
   *
   * @param value
   */
  set user(value: UserStatePayload) {
    // Store the value
    this._isStateDefined = true;
    this._userStore.setUser(value);
  }

  get user$(): Observable<User> {
    return this._user$;
  }
  /**
   *
   * Retornar dados do usuário logado
   *
   */
  get(): Observable<UserStateModel> {
    if (!this._isStateDefined) {
      const localStorageUser = this.getUserFromLocalStorage();
      // Se houver no localstorage
      if (localStorageUser) {
        // Retornar do localstorage
        return of(localStorageUser);
      }

      // Retornar do backend
      return this._httpClient.get<UserStateModel>(`${environment.apiUrl}/me`).pipe(
        tap((user) => {
          this.user = user;
        }),
      );
    }

    // Emissão SÍNCRONA, de propósito. `RoleGuard` consome isto num `canActivate`:
    // devolver `user$` (que é `toObservable`) trava a navegação, porque
    // `toObservable` só emite no flush de efeito — e o flush espera o bootstrap,
    // que espera esta mesma navegação. O `select` do NGXS emitia na inscrição;
    // `toObservable` não. Para fluxo reativo, use `user$` direto.
    return of(this._userStore.getUser());
  }

  getUserFromLocalStorage() {
    // Carregar do localstorage
    const userString = localStorage.getItem('user');
    // Se houver no localstorage
    if (!userString) {
      return;
    }
    try {
      return JSON.parse(userString);
    } catch {
      // Snapshot corrompido não pode derrubar o bootstrap (este método roda no
      // construtor): descarta a chave inválida e degrada para "sem usuário".
      localStorage.removeItem('user');
      return;
    }
  }

  /**
   *
   * Atualizar usuário
   *
   */
  update(user: UserStatePayload): Observable<any> {
    // O PATCH /me devolve `{ user, accessToken }` (ver auth.controller.ts), não o
    // usuário cru. Gravar o envelope inteiro faz `roles`/`currentCompanyId`
    // sumirem — e é o localStorage que os guards leem.
    return this._httpClient
      .patch<{ user: UserStateModel; accessToken?: string }>(`${environment.apiUrl}/me`, { user })
      .pipe(
        map((response) => {
          // Token ANTES do usuário: setar `this.user` muda o signal
          // `currentCompanyId`, e qualquer refetch de recurso por empresa disparado
          // por essa mudança precisa já enxergar o token novo (mesma ordem que o
          // company-select documenta). Um PATCH que altera `currentCompanyId` emite
          // token novo; descartá-lo deixaria o accessToken defasado no próximo request.
          if (response?.accessToken) {
            localStorage.setItem('accessToken', response.accessToken);
          }
          if (response?.user) {
            this.user = response.user;
          }
        }),
      );
  }
}
